import { useState, type ReactNode } from "react";
import { gameDate, monogram, nickname, teamHue } from "../lib/format";

/** What a card needs, shared by a logged event and an Upcoming watchlist row. */
export interface GameLike {
  event_date: string;
  league: string;
  /** The sport's emoji, from the server ("🏒"); empty for an unknown sport. */
  sport_emoji?: string;
  home_team: string;
  away_team: string;
  home_score?: number | null;
  away_score?: number | null;
  venue_name: string | null;
  venue_city: string | null;
  game_type?: string;
  special?: { label: string; emoji: string; icon_url: string | null } | null;
}

/** A team's cached logo, or a colored monogram when there is none yet. */
export function TeamMark({ team, logo }: { team: string; logo?: string }) {
  const [broken, setBroken] = useState(false);
  if (logo && !broken) {
    return (
      <img
        src={logo}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="h-8 w-8 shrink-0 object-contain"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full font-display text-[11px] font-bold tracking-wide text-white"
      style={{ background: `hsl(${teamHue(team)} 32% 34%)` }}
    >
      {monogram(team)}
    </span>
  );
}

function TeamName({
  team,
  logo,
  side,
  dim,
}: {
  team: string;
  logo?: string;
  side: "away" | "home";
  dim: boolean;
}) {
  // A phone shows the nickname ("Lightning"); from sm up there is room for
  // the full name, truncated if it still does not fit.
  const name = (
    <span className={`min-w-0 truncate ${dim ? "font-medium text-muted" : "font-semibold"}`}>
      <span className="sm:hidden">{nickname(team)}</span>
      <span className="hidden sm:inline">{team}</span>
    </span>
  );
  return (
    <div className={`flex min-w-0 items-center gap-2 ${side === "away" ? "justify-end text-right" : ""}`}>
      {side === "away" ? (
        <>
          {name}
          <TeamMark team={team} logo={logo} />
        </>
      ) : (
        <>
          <TeamMark team={team} logo={logo} />
          {name}
        </>
      )}
    </div>
  );
}

/** "2 – 1", winner bright and loser dimmed, or "VS" before there is a score. */
export function Score({ away, home }: { away?: number | null; home?: number | null }) {
  if (away == null || home == null) {
    return <span className="font-display text-base tracking-[0.12em] text-muted">VS</span>;
  }
  return (
    <span className="min-w-16 text-center font-display text-[26px] leading-none font-bold tabular-nums">
      <span className={away > home ? "" : "opacity-45"}>{away}</span>
      <span className="mx-1 opacity-35">–</span>
      <span className={home > away ? "" : "opacity-45"}>{home}</span>
    </span>
  );
}

/**
 * One game: league and date, the matchup, the venue, then whatever the page
 * adds below (who went, an RSVP row). Everything shown is what the server
 * sent; the card decides nothing.
 */
export function GameCard({
  game: g,
  logos,
  venueImages,
  extra,
  footer,
}: {
  game: GameLike;
  logos?: Record<string, string>;
  venueImages?: Record<string, string>;
  extra?: ReactNode;
  footer?: ReactNode;
}) {
  const has = g.home_score != null && g.away_score != null;
  const place = [g.venue_name, g.venue_city].filter(Boolean).join(" · ");
  const venueImg = g.venue_name ? venueImages?.[g.venue_name] : undefined;
  return (
    <article className="grid gap-2 rounded-2xl bg-surface p-3.5">
      {g.special && (
        <span className="inline-flex items-center gap-1.5 justify-self-start rounded-full border border-gold/35 bg-gold/10 py-0.5 pr-2.5 pl-1.5 text-[12.5px] font-semibold">
          {g.special.icon_url ? (
            <img src={g.special.icon_url} alt="" referrerPolicy="no-referrer" className="h-5 w-5 object-contain" />
          ) : (
            <span aria-hidden>{g.special.emoji}</span>
          )}
          {g.special.label}
        </span>
      )}
      <div className="flex min-w-0 items-center gap-2 text-[12.5px] text-muted">
        <span className="rounded bg-raised px-1.5 py-0.5 font-display text-xs font-semibold tracking-wider text-neutral-300 uppercase">
          {g.sport_emoji ? `${g.sport_emoji} ` : ""}
          {g.league}
        </span>
        <span className="truncate">
          {gameDate(g.event_date)}
          {g.game_type && g.game_type !== "Regular Season" && (
            <>
              {" · "}
              <span className="text-soon">{g.game_type}</span>
            </>
          )}
        </span>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2.5">
        <TeamName
          team={g.away_team}
          logo={logos?.[g.away_team]}
          side="away"
          dim={has && (g.away_score ?? 0) <= (g.home_score ?? 0)}
        />
        <Score away={g.away_score} home={g.home_score} />
        <TeamName
          team={g.home_team}
          logo={logos?.[g.home_team]}
          side="home"
          dim={has && (g.home_score ?? 0) <= (g.away_score ?? 0)}
        />
      </div>
      {place && (
        <div className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-muted">
          {venueImg ? (
            <img src={venueImg} alt="" referrerPolicy="no-referrer" className="h-4.5 w-4.5 object-contain" />
          ) : (
            <span aria-hidden>📍</span>
          )}
          <span className="truncate">{place}</span>
        </div>
      )}
      {extra}
      {footer && <div className="border-t border-line/70 pt-2">{footer}</div>}
    </article>
  );
}
